/*
  The looks shared by WrButton, WrAnchor and WrLinkTo.

  The variant names describe the look, not the HTML element: a WrLinkTo can
  look like a primary button, and a WrButton can look like a link.
*/
export const ACTION_VARIANTS = [
  "primary",
  "primary-white-border",
  "outlined",
  "outlined-mini",
  "link",
  "inverted-link"
] as const;

export type ActionVariant = (typeof ACTION_VARIANTS)[number];

const BUTTON_LOOK = [
  "font-sans",
  "gap-2",
  "inline-flex",
  "items-center",
  "justify-center",
  "rounded-md",
  "text-p1",
  "focus-visible:outline",
  "focus-visible:outline-2",
  "focus-visible:outline-offset-0"
];

const BORDERED_BUTTON_LOOK = [
  ...BUTTON_LOOK,
  "active:bg-gradient-to-r",
  "border",
  "hover:shadow",
  "px-4",
  "py-2"
];

const LINK_LOOK = [
  "hover:underline",
  "focus-visible:outline-none",
  "focus-visible:underline"
];

const LOOKS: Record<ActionVariant, string[]> = {
  primary: [
    ...BORDERED_BUTTON_LOOK,
    "active:from-purple",
    "active:to-pink",
    "bg-pink",
    "border-pink",
    "text-white",
    "focus-visible:border-purple",
    "focus-visible:outline-purple"
  ],
  "primary-white-border": [
    ...BORDERED_BUTTON_LOOK,
    "active:from-purple",
    "active:to-pink",
    "bg-transparent",
    "border-white",
    "text-white"
  ],
  outlined: [
    ...BORDERED_BUTTON_LOOK,
    "[&>svg]:text-grey",
    "active:from-grey-medium",
    "active:to-white",
    "bg-white",
    "border-grey-medium",
    "focus-visible:border-black",
    "focus-visible:outline-black"
  ],
  "outlined-mini": [
    ...BUTTON_LOOK,
    "[&>svg]:h-6",
    "[&>svg]:text-grey",
    "[&>svg]:w-6",
    "active:from-grey-medium",
    "active:to-white",
    "bg-white",
    "border-none",
    "hover:bg-grey-medium",
    "p-1",
    "focus-visible:outline-black"
  ],
  link: [...LINK_LOOK, "focus-visible:text-pink"],
  "inverted-link": [...LINK_LOOK, "text-white", "focus-visible:text-purple"]
};

/*
  Extra styles of a <button>. It always lays out its icon and text in a row,
  and the looks of a button have a disabled state (a link has none).
*/
const BUTTON_ELEMENT_BASE = [
  "gap-2",
  "inline-flex",
  "items-center",
  "justify-center"
];

const BUTTON_DISABLED = [
  "active:disabled:bg-none",
  "disabled:bg-white",
  "disabled:border-grey-medium",
  "disabled:text-grey-medium",
  "hover:disabled:shadow-none"
];

/*
  Extra styles of an <a>: the app styles links on hover (underline and
  colour), which a link that looks like a button must not get.
*/
const LINK_ELEMENT_EXTRAS: Record<ActionVariant, string[]> = {
  primary: ["hover:no-underline"],
  "primary-white-border": ["hover:no-underline"],
  outlined: ["hover:no-underline", "hover:text-black"],
  "outlined-mini": ["hover:no-underline"],
  link: ["inline-block", "hover:text-black"],
  "inverted-link": ["inline-block"]
};

/** The classes of a <button> with the given look */
export function buttonStyles(variant: ActionVariant): string {
  const isLinkLook = variant === "link" || variant === "inverted-link";
  const classes = [
    ...BUTTON_ELEMENT_BASE,
    ...LOOKS[variant],
    ...(isLinkLook ? [] : BUTTON_DISABLED)
  ];

  return [...new Set(classes)].join(" ");
}

/** The classes of an <a> (WrAnchor, WrLinkTo) with the given look */
export function linkStyles(variant: ActionVariant): string {
  return [...LOOKS[variant], ...LINK_ELEMENT_EXTRAS[variant]].join(" ");
}
